import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import HomePage from './pages/HomePage';
import FamilyTreePage from './pages/FamilyTreePage';
import GalleryPage from './pages/GalleryPage';
import VideosPage from './pages/VideosPage';
import PhoneBookPage from './pages/PhoneBookPage';
import styled from 'styled-components';
import HomeBtn from './images/home_btn.png';
import Candle from './images/candle_btn.png';
import Diagram from './images/diagram_btn.png';
import Gallery from './images/gallery_btn.png';
import Stories from './images/stories_btn.png';
import Videos from './images/videos_btn.png';
import Phones from './images/phones_btn.png';
import Bg from './images/bg.png';

const NavContainer = styled.nav<{ isHidden: boolean }>`
  position: fixed;
  top: 390px;
  right: ${(props) => (props.isHidden ? '-50px' : '0')};
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1001;
  padding: 9px 5px;
  background-image: url(${Bg});
  border-radius: 8px 0 0 8px;
  transition: right 0.3s ease;
  touch-action: none; /* למנוע חיכוכים עם תנועות מגע */
  width: 55px; /* גודל הפאנל כשמסתיר */
  height: auto;
  cursor: pointer; /* מצביע על כך שהפאנל ניתן לגרירה */
`;

const NavButton = styled(Link)<{ imageUrl: string }>`
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
  width: 50px;
  height: 50px;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s;
  cursor: pointer;
  background-color: #b5ced9;
  border-radius: 8px;

  &:hover {
    background-color: rgba(162, 216, 240, 1);
  }
`;

export const Navigation = () => {
  const [isHidden, setIsHidden] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // משתנה לשמירה על ה-Timer
  const panelRef = useRef<HTMLDivElement | null>(null); // משתנה לשמירה על הפאנל

  // פונקציה לפתיחה/סגירה בלחיצה
  const togglePanel = () => {
    setIsHidden(!isHidden);
    resetTimeout(); // מחדש את הזמן אחרי כל לחיצה
  };

  // פונקציה לגילוי גרירה של הפאנל
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setDragStartX(touch.clientX);
    setIsDragging(true);
    resetTimeout(); // מחדש את הזמן אחרי כל גרירה
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const distance = dragStartX - touch.clientX; // כיוון גרירה: מימין לשמאל
    if (distance < 50) {
      setIsHidden(true); // אם הגרירה הייתה מספיק חזקה, מסתירים
    } else if (distance > -50) {
      setIsHidden(false); // אם הגרירה הייתה בכיוון ההפוך, מציגים את הפאנל
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    resetTimeout(); // מחדש את הזמן אחרי כל סיום גרירה
  };

  // פונקציה להמתין 3 שניות ולהסוות את הפאנל
  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current); // אם יש כבר טיימר פעיל, מנקים אותו
    }
    timeoutRef.current = setTimeout(() => {
      setIsHidden(true); // סוגרים את הפאנל אחרי 3 שניות של חוסר פעילות
    }, 3000); // זמן המתנה של 3 שניות
  };

  // טיפול בהחזרת הפאנל כאשר העכבר עומד מעל החלק הגלוי
  const handleMouseEnter = () => {
    if (isHidden) {
      setIsHidden(false);
      resetTimeout(); // מחדש את הזמן אחרי הצגת הפאנל
    }
  };

  const handleMouseLeave = () => {
    resetTimeout(); // מחדש את הזמן אם העכבר יצא
  };

  useEffect(() => {
    resetTimeout(); // עלינו להפעיל את הטיימר כשהרכיב נטען
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current); // מנקים את הטיימר כשהרכיב מוסר
      }
    };
  }, []);

  return (
    <>
      <NavContainer
        ref={panelRef}
        isHidden={isHidden}
        onClick={togglePanel} // בלחיצה על הפאנל, הפאנל יתממש
        onTouchStart={handleTouchStart} // התחלת גרירה
        onTouchMove={handleTouchMove} // גרירה
        onTouchEnd={handleTouchEnd} // סיום גרירה
        onMouseEnter={handleMouseEnter} // אם העכבר מעל הפאנל, פתח את הפאנל
        onMouseLeave={handleMouseLeave} // אם העכבר יצא מהפאנל, סגור את הפאנל אחרי זמן
      >
        <NavButton to="/" imageUrl={HomeBtn} />
        <NavButton to="/family-tree" imageUrl={Diagram} />
        <NavButton to="/gallery" imageUrl={Gallery} />
        <NavButton to="/videos" imageUrl={Videos} />
        <NavButton to="/phonebook" imageUrl={Phones} />
        <NavButton to="/stories" imageUrl={Stories} />
        <NavButton to="/candle" imageUrl={Candle} />
      </NavContainer>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/family-tree" element={<FamilyTreePage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/phonebook" element={<PhoneBookPage />} />
      </Routes>
    </>
  );
};
